import { useApolloClient } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, useTheme } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { SyntheticEvent, useCallback } from 'react';
import { EllipsesMenu } from 'src/components';
import { getHeadToolSx } from 'src/components/Navbar/DesktopDrawer/InboxPane/inbox-pane.styles';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { getCachedAggregation } from 'src/graphql/entities/conversation-aggregations';
import { GlobalTab, useGlobalTabs } from 'src/utils/hooks';
import {
  getTabsToClose,
  GetUnseenCount,
  TabCloseAction,
} from 'src/utils/hooks/get-tabs-to-close';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { TABS_ENABLED_ON_ROUTES } from './enabled-routes';
import { GlobalTabsBar, GlobalTabsContainer, Tab } from './styled';
import { TabLabel } from './tab-labels';

const TAB_MENU: { action: TabCloseAction; label: string; icon: string }[] = [
  { action: 'all', label: 'Close all tabs', icon: 'x' },
  { action: 'other', label: 'Close other tabs', icon: 'copy-x' },
  { action: 'caughtUp', label: 'Close caught-up tabs', icon: 'check-check' },
];

export const GlobalTabs = () => {
  const { isMobile } = useScreenDimensions();
  const { pathnameWithoutPrefix } = useNavigateRoute();
  const theme = useTheme();
  const client = useApolloClient();
  const { activeTab, navigateToTab, tabs, closeTab, closeTabs } = useGlobalTabs();
  const { groupRules } = useGroupRules();
  const accessToTabsInProvision = groupRules[UserGroupRules.AccessToTabsInProvision];

  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      const tab = tabs.find((item) => item.id === value);
      if (tab) navigateToTab(tab);
    },
    [navigateToTab, tabs],
  );
  const onClose = useCallback(
    (tab: GlobalTab) => (event: SyntheticEvent) => {
      event.stopPropagation();
      closeTab(tab);
    },
    [closeTab],
  );

  const getUnseenCount: GetUnseenCount = useCallback(
    (tab) => {
      if (tab.type !== 'conversation') return null;
      const record = getCachedAggregation(client.cache, tab.id);
      return record ? record.unseenCount : null;
    },
    [client.cache],
  );

  const menuOptions = TAB_MENU.map(({ action, label, icon }) => {
    const closingTabs = getTabsToClose({
      tabs,
      activeId: activeTab?.id ?? null,
      action,
      getUnseenCount,
    });
    return {
      label: `${label} (${closingTabs.length})`,
      icon,
      iconLibrary: 'lucide' as const,
      disabled: closingTabs.length === 0,
      onClick: () => {
        closeTabs(closingTabs);
      },
    };
  });

  if (!accessToTabsInProvision) return null;
  // Ensure tabs rendered for specific views only.
  if (!TABS_ENABLED_ON_ROUTES.some((route) => pathnameWithoutPrefix.startsWith(route))) {
    return null;
  }
  if (!tabs.length) return null;
  if (isMobile) return null;
  return (
    <GlobalTabsBar data-testid="global-tabs">
      <GlobalTabsContainer
        value={activeTab?.id || false}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            data-testid="global-tab"
            label={
              <>
                <TabLabel {...tab} />
                <IconButton
                  className="close-tab-button"
                  size="small"
                  component="div"
                  onClick={onClose(tab)}
                >
                  <CloseIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </>
            }
          />
        ))}
      </GlobalTabsContainer>
      <V2ThemeProvider>
        <EllipsesMenu
          options={menuOptions}
          icon="more-vertical"
          iconLibrary="lucide"
          title="Tab options"
          triggerTestId="tab-options"
          sx={{
            ...getHeadToolSx(theme),
            flexShrink: 0,
            alignSelf: 'center',
          }}
        />
      </V2ThemeProvider>
    </GlobalTabsBar>
  );
};
